import React, { useEffect, useRef, useState } from "react";
import { Link } from "gatsby";
import { BASE_SITE_URL } from "../../shared/constants";
import {
  FacebookShareButton,
  LinkedinShareButton,
  TwitterShareButton,
} from "react-share";
import { FaFacebookF, FaLinkedinIn, FaTwitter } from "react-icons/fa";

function TableContent({ objectData, content = "", articleContent }) {
  const [headings, setHeadings] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const observerRef = useRef(null);

  useEffect(() => {
    if (!content) return;

    const extractedHeadings = [];
    const parser = new DOMParser();
    const doc = parser.parseFromString(content, "text/html");

    let currentSection = null;
    let index = 0;

    doc.querySelectorAll(".toc2, .toc3").forEach((element) => {
      const level = element.tagName === "H2" ? 2 : 3;
      const text = element.textContent.trim();
      const id = element.id || `heading-${index}`;

      if (level === 2) {
        currentSection = { id, text, children: [] };
        extractedHeadings.push(currentSection);
      } else if (level === 3 && currentSection) {
        currentSection.children.push({ id, text });
      }

      index++;
    });

    setHeadings(extractedHeadings);
  }, [content]);

  // Highlight active TOC section on scroll
  useEffect(() => {
    if (!headings.length || typeof window === "undefined") return;

    if (observerRef.current) observerRef.current.disconnect();

    const allIds = headings.flatMap((h) => [
      h.id,
      ...h.children.map((c) => c.id),
    ]);

    observerRef.current = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting);
        if (visible.length) setActiveId(visible[0].target.id);
      },
      { rootMargin: "-20% 0px -70% 0px", threshold: 0 },
    );

    allIds.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observerRef.current.observe(el);
    });

    return () => observerRef.current && observerRef.current.disconnect();
  }, [headings]);

  const handleScroll = (e, targetId, offset = 88) => {
    e.preventDefault();
    const el = document.getElementById(targetId);
    if (el) {
      window.scrollTo({
        top: el.getBoundingClientRect().top + window.scrollY - offset,
        behavior: "smooth",
      });
    }
  };

  const articleUrl = `${BASE_SITE_URL}/article/${articleContent?.slug ?? ""}`;

  return (
    <div className="article-post-main-container">
      <div className="article-post-container">
        {/* ---- Left: article body + share ---- */}
        <div className="article-post-details">
          <div
            className="article-post-body-block"
            dangerouslySetInnerHTML={{ __html: content }}
          />

          <div className="article-share-container">
            <span className="article-share-label">Share this article</span>
            <div className="article-share-card">
              <div className="share-card-icon-wrapper">
                <LinkedinShareButton url={articleUrl}>
                  <FaLinkedinIn color="#fff" size="16" />
                </LinkedinShareButton>
              </div>
              <div className="share-card-icon-wrapper">
                <FacebookShareButton url={articleUrl}>
                  <FaFacebookF color="#fff" size="16" />
                </FacebookShareButton>
              </div>
              <div className="share-card-icon-wrapper">
                <TwitterShareButton url={articleUrl}>
                  <FaTwitter color="#fff" size="16" />
                </TwitterShareButton>
              </div>
            </div>
          </div>
        </div>

        {/* ---- Right: sticky sidebar (TOC + related articles) ---- */}
        <aside className="article-post-sidebar">
          {headings.length > 0 && (
            <nav className="article-toc" aria-label="Table of contents">
              <p className="article-toc__label">On this page</p>
              <ul className="article-toc__list">
                {headings.map((heading) => (
                  <li key={heading.id} className="article-toc__item">
                    <a
                      href={`#${heading.id}`}
                      className={`article-toc__link${
                        activeId === heading.id
                          ? " article-toc__link--active"
                          : ""
                      }`}
                      onClick={(e) => handleScroll(e, heading.id)}
                    >
                      {heading.text}
                    </a>
                    {heading.children.length > 0 && (
                      <ul className="article-toc__sub-list">
                        {heading.children.map((child) => (
                          <li key={child.id}>
                            <a
                              href={`#${child.id}`}
                              className={`article-toc__sub-link${
                                activeId === child.id
                                  ? " article-toc__link--active"
                                  : ""
                              }`}
                              onClick={(e) => handleScroll(e, child.id)}
                            >
                              {child.text}
                            </a>
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                ))}
              </ul>
            </nav>
          )}

          {objectData && objectData.length > 0 && (
            <div className="article-sidebar-related">
              <p className="article-sidebar-related__label">Related Articles</p>
              <ul className="article-sidebar-related__list">
                {objectData.slice(0, 4).map((item, i) => (
                  <li key={i} className="article-sidebar-related__item">
                    <Link
                      to={`/${item.slug_type}/${item.slug}/`}
                      className="article-sidebar-related__link"
                    >
                      {item.og_image && (
                        <img
                          src={item.og_image}
                          alt={item.imageAltTxt || item.title}
                          className="article-sidebar-related__thumb"
                        />
                      )}
                      <span className="article-sidebar-related__title">
                        {item.title}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}

export default TableContent;
